import { Controller } from "@hotwired/stimulus"

// Highlights the table of contents entry of the section being read (blog post inline + sidebar copies, health guide "Sommaire")
// and scrolls it into view inside its own scrollable list (blog sidebar, FAQ themes strip) without moving the page.
const REVEAL_MARGIN = 16

export default class extends Controller {
  static targets = ["link"]
  static classes = ["active"]
  static values = { offset: { type: Number, default: 0.3 } }

  connect () {
    this.frame = null
    this.currentId = undefined
    this.update()
  }

  disconnect () {
    if (this.frame) window.cancelAnimationFrame(this.frame)
  }

  onScroll () {
    if (this.frame) return

    this.frame = window.requestAnimationFrame(() => {
      this.frame = null
      this.update()
    })
  }

  update () {
    const threshold = window.innerHeight * this.offsetValue
    let currentId = null

    for (const heading of this.headings()) {
      if (heading.getBoundingClientRect().top > threshold) break
      currentId = heading.id
    }

    if (currentId === this.currentId) return
    this.currentId = currentId

    this.linkTargets.forEach((link) => {
      const active = decodeURIComponent(link.hash.slice(1)) === currentId

      link.classList.toggle(this.activeClass, active)
      if (active) {
        link.setAttribute("aria-current", "location")
        this.reveal(link)
      } else {
        link.removeAttribute("aria-current")
      }
    })
  }

  // Not `scrollIntoView`: it also scrolls the window, fighting the reader's own scroll.
  reveal (link) {
    const container = this.scrollContainer(link)
    if (!container) return

    const box = container.getBoundingClientRect()
    const target = link.getBoundingClientRect()
    let top = container.scrollTop
    let left = container.scrollLeft

    if (target.top < box.top + REVEAL_MARGIN) top -= box.top + REVEAL_MARGIN - target.top
    if (target.bottom > box.bottom - REVEAL_MARGIN) top += target.bottom - (box.bottom - REVEAL_MARGIN)
    if (target.left < box.left + REVEAL_MARGIN) left -= box.left + REVEAL_MARGIN - target.left
    if (target.right > box.right - REVEAL_MARGIN) left += target.right - (box.right - REVEAL_MARGIN)

    if (top !== container.scrollTop || left !== container.scrollLeft) container.scrollTo({ top, left, behavior: "smooth" })
  }

  scrollContainer (link) {
    for (let node = link.parentElement; node && this.element.contains(node); node = node.parentElement) {
      const style = window.getComputedStyle(node)
      const scrollsY = /auto|scroll/.test(style.overflowY) && node.scrollHeight > node.clientHeight
      const scrollsX = /auto|scroll/.test(style.overflowX) && node.scrollWidth > node.clientWidth

      if (scrollsY || scrollsX) return node
    }

    return null
  }

  headings () {
    const ids = new Set(this.linkTargets.map((link) => decodeURIComponent(link.hash.slice(1))))

    return Array.from(ids).map((id) => document.getElementById(id)).filter(Boolean)
  }
}
